import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@cloudgenie/ui';
import { API_BASE, useAuth, hasCap } from '../AuthContext';
import { AdminChrome } from './AdminPage';

/** Wave 5 Phase 5 — Tenant detail: entitlements editor + usage + impersonation.
 *  Access: capability `org.manage` (OWNER role). */

interface TenantDetail {
  tenant_id: string;
  tenant: Record<string, unknown>;
  entitlements: Record<string, unknown>;
  subscription?: Record<string, unknown> | null;
  usage_recent: Array<{ tenant_id: string; meter: string; day: string; quantity: number; event_count: number }>;
}

export default function AdminTenantPage() {
  const { user, loading: authLoading } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editKey, setEditKey] = useState('');
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const [impUserId, setImpUserId] = useState('');
  const [impReason, setImpReason] = useState('');
  const [impBusy, setImpBusy] = useState(false);
  const [impStatus, setImpStatus] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading || !user || !id) return;
    if (!hasCap(user, 'admin.ops')) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/admin/tenants/${encodeURIComponent(id)}`, { credentials: 'include' });
        if (!r.ok) throw new Error(`${r.status}`);
        setDetail(await r.json());
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [authLoading, user, id]);

  async function savePatch(key: string, value: unknown) {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/admin/tenants/${encodeURIComponent(id)}/entitlements`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ features: { [key]: value } }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : `HTTP ${r.status}`);
      }
      const data = await r.json();
      setDetail((d) => (d ? { ...d, entitlements: data.features } : d));
      setEditKey('');
      setEditValue('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function startImpersonate() {
    if (!impUserId.trim() || impReason.trim().length < 3) {
      setImpStatus('Provide user_id + a reason (≥3 chars).');
      return;
    }
    setImpBusy(true);
    setImpStatus(null);
    try {
      const r = await fetch(`${API_BASE}/api/admin/impersonate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: impUserId.trim(), reason: impReason.trim() }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : `HTTP ${r.status}`);
      }
      const data = await r.json();
      setImpStatus(`Impersonating ${data.target_user_id} until ${data.expires_at}. Reload the page to continue as them.`);
    } catch (e) {
      setImpStatus((e as Error).message);
    } finally {
      setImpBusy(false);
    }
  }

  if (authLoading) {
    return <AdminChrome title="Tenant"><p className="text-center py-16 text-muted-foreground">Loading…</p></AdminChrome>;
  }
  if (!user) { window.location.href = `/login?next=${encodeURIComponent(`/admin/tenants/${id || ''}`)}`; return null; }
  if (!hasCap(user, 'admin.ops')) {
    return <AdminChrome title="Tenant"><p className="text-center py-16 text-muted-foreground" data-testid="tenant-detail-forbidden">Missing org.manage capability.</p></AdminChrome>;
  }

  return (
    <AdminChrome title={`Tenant · ${id}`}>
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-8" data-testid="admin-tenant-page">
        <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground">← All tenants</Link>
        <h1 className="text-3xl font-semibold mt-2">Tenant {id}</h1>

        {error && <p role="alert" className="mt-4 text-sm text-red-400" data-testid="admin-tenant-error">{error}</p>}

        {detail && (
          <>
            <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="tenant-meta">
              <MetaCard label="Plan" value={String(detail.tenant.plan ?? '—')} />
              <MetaCard label="Status" value={String(detail.tenant.status ?? '—')} />
              <MetaCard label="Name" value={String(detail.tenant.name ?? '—')} />
            </div>

            {/* Entitlements editor */}
            <div className="mt-10">
              <h2 className="text-xl font-semibold mb-3">Entitlements</h2>
              <div className="rounded-xl border border-border bg-card p-4" data-testid="entitlements-editor">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left px-2 py-2">Feature key</th>
                      <th className="text-left px-2 py-2">Value</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(detail.entitlements).map(([k, v]) => (
                      <tr key={k} className="border-t border-border/40" data-testid={`entitlement-${k}`}>
                        <td className="px-2 py-2 font-mono text-xs">{k}</td>
                        <td className="px-2 py-2">{formatValue(v)}</td>
                        <td className="px-2 py-2 text-right">
                          <button
                            onClick={() => savePatch(k, null)}
                            disabled={saving}
                            data-testid={`entitlement-remove-${k}`}
                            className="text-xs text-red-400 hover:underline disabled:opacity-40"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="entitlement-add-row">
                  <input
                    placeholder="feature.key"
                    value={editKey}
                    onChange={(e) => setEditKey(e.target.value)}
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                    data-testid="entitlement-key-input"
                  />
                  <input
                    placeholder="value (true / false / integer)"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm flex-1 min-w-[200px]"
                    data-testid="entitlement-value-input"
                  />
                  <Button
                    variant="primary" size="sm"
                    onClick={() => savePatch(editKey.trim(), coerce(editValue))}
                    disabled={saving || !editKey.trim()}
                    data-testid="entitlement-save"
                  >
                    {saving ? 'Saving…' : 'Save override'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Impersonation */}
            <div className="mt-10">
              <h2 className="text-xl font-semibold mb-3">Impersonate a user</h2>
              <div className="rounded-xl border border-red-700/40 bg-red-900/10 p-4" data-testid="impersonation-block">
                <p className="text-sm text-muted-foreground mb-3">
                  Every impersonation is logged to the audit trail. Provide a reason.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    placeholder="target user_id"
                    value={impUserId}
                    onChange={(e) => setImpUserId(e.target.value)}
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                    data-testid="impersonate-user-id"
                  />
                  <input
                    placeholder="reason (audit-logged)"
                    value={impReason}
                    onChange={(e) => setImpReason(e.target.value)}
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm flex-1 min-w-[220px]"
                    data-testid="impersonate-reason"
                  />
                  <Button
                    variant="outline" size="sm"
                    onClick={startImpersonate}
                    disabled={impBusy}
                    data-testid="impersonate-submit"
                  >
                    {impBusy ? 'Working…' : 'Impersonate'}
                  </Button>
                </div>
                {impStatus && <p className="mt-3 text-sm text-yellow-200" data-testid="impersonate-status">{impStatus}</p>}
              </div>
            </div>

            {/* Usage summary */}
            <div className="mt-10">
              <h2 className="text-xl font-semibold mb-3">Recent daily usage (last 50 rows)</h2>
              <div className="overflow-x-auto rounded-xl border border-border bg-card">
                <table className="w-full text-sm" data-testid="tenant-usage-table">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-2">Day</th>
                      <th className="text-left px-3 py-2">Meter</th>
                      <th className="text-right px-3 py-2">Quantity</th>
                      <th className="text-right px-3 py-2">Events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.usage_recent.length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">No usage yet.</td></tr>
                    )}
                    {detail.usage_recent.map((u, i) => (
                      <tr key={`${u.day}-${u.meter}-${i}`} className="border-t border-border/40">
                        <td className="px-3 py-2 font-mono text-xs">{u.day}</td>
                        <td className="px-3 py-2 font-mono text-xs">{u.meter}</td>
                        <td className="px-3 py-2 text-right">{u.quantity}</td>
                        <td className="px-3 py-2 text-right">{u.event_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </section>
    </AdminChrome>
  );
}

function MetaCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === true) return '✓';
  if (v === false) return '✗';
  if (v === null || v === undefined) return '—';
  return String(v);
}

function coerce(raw: string): unknown {
  const s = raw.trim();
  if (s === '') return true;
  if (s.toLowerCase() === 'true') return true;
  if (s.toLowerCase() === 'false') return false;
  if (s.toLowerCase() === 'null') return null;
  if (/^-?\d+$/.test(s)) return parseInt(s, 10);
  return s;
}
